import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionUnitListQuery,
	useUpdateFieldOptionUnitDetailOrderMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import DndArticleListSection from './components/re-order/DndArticleListSection';
import { arrayMove } from '@dnd-kit/sortable';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

export default function UnitOrderPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { fieldId } = useParams();
	const [updateFieldOptionUnitDetailOrder] =
		useUpdateFieldOptionUnitDetailOrderMutation();

	const [meditationItems, setMeditationItems] = useState<any>([]);

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionUnitListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				setMeditationItems(propertySummary?.data?.items || []);
			}
		}
	}, [propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setMeditationItems((prev: unknown[]) =>
			arrayMove(prev, activeIndex, overIndex)
		);
	};

	const handleSubmit = async () => {
		const response = await updateFieldOptionUnitDetailOrder(
			(meditationItems || []).map((item: any) => item.id)
		);
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitOrderSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(`/field-management/field-management-list/unit/${fieldId}`);
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.unitStatus')}</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<DndArticleListSection
								meditationItems={transformResponse(meditationItems) ?? []}
								handleRepositionItem={handleRepositionItem}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(`/field-management/field-management-list/unit/${fieldId}`);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
